import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useRef, Suspense } from 'react'
import { Toaster } from 'sonner'
import { Layout } from './components/Layout'
import { LoadingSpinner } from './components/ui/loading-spinner'
import { useTelegram } from './telegram/TelegramProvider'
import { lazyWithRetry } from './lib/lazy-with-retry'

// Lazy-роуты с retry: при сетевом сбое чанк перезапрашивается, затем — reload страницы
const Catalog = lazyWithRetry(() => import('./pages/Catalog').then((m) => ({ default: m.Catalog })))
const GiveawayDetails = lazyWithRetry(() => import('./pages/GiveawayDetails').then((m) => ({ default: m.GiveawayDetails })))
const Profile = lazyWithRetry(() => import('./pages/Profile').then((m) => ({ default: m.Profile })))
const CreateGiveaway = lazyWithRetry(() => import('./pages/CreateGiveaway').then((m) => ({ default: m.CreateGiveaway })))
const MyGiveaways = lazyWithRetry(() => import('./pages/MyGiveaways').then((m) => ({ default: m.MyGiveaways })))
const MyChannels = lazyWithRetry(() => import('./pages/MyChannels').then((m) => ({ default: m.MyChannels })))
const ChannelAnalytics = lazyWithRetry(() => import('./pages/ChannelAnalytics').then((m) => ({ default: m.ChannelAnalytics })))
const GiveawayTrust = lazyWithRetry(() => import('./pages/GiveawayTrust').then((m) => ({ default: m.GiveawayTrust })))
const PostTemplates = lazyWithRetry(() => import('./pages/PostTemplates').then((m) => ({ default: m.PostTemplates })))
const Subscription = lazyWithRetry(() => import('./pages/Subscription').then((m) => ({ default: m.Subscription })))
const JoinCaptcha = lazyWithRetry(() => import('./pages/JoinCaptcha').then((m) => ({ default: m.JoinCaptcha })))

function PageFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <LoadingSpinner size="lg" />
    </div>
  )
}

function AppRoutes() {
  const navigate = useNavigate()
  const location = useLocation()
  const { startParam } = useTelegram()
  const hasRedirectedFromStartParam = useRef(false)

  useEffect(() => {
    // Редирект по startParam только один раз за сессию. Иначе при переходе
    // "Вернуться к каталогу" или по нижнему меню эффект снова срабатывал
    // и возвращал на страницу конкурса.
    if (!startParam) return
    if (hasRedirectedFromStartParam.current) return

    if (startParam.startsWith('joincaptcha_')) {
      const nonce = startParam.slice('joincaptcha_'.length)
      if (!nonce) return
      const targetPath = `/join-captcha/${nonce}`
      hasRedirectedFromStartParam.current = true
      if (location.pathname !== targetPath) {
        navigate(targetPath, { replace: true, state: { fromStartParam: true } })
      }
      return
    }

    if (!startParam.startsWith('giveaway_')) return

    const parts = startParam.replace('giveaway_', '').split('_')
    const giveawayId = parts[0]
    if (!giveawayId) return

    const targetPath = `/giveaway/${giveawayId}`
    if (location.pathname === targetPath) {
      hasRedirectedFromStartParam.current = true
      return
    }

    hasRedirectedFromStartParam.current = true
    navigate(targetPath, { replace: true, state: { fromStartParam: true } })
  }, [startParam, navigate, location.pathname])

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Catalog />} />
          <Route path="/giveaway/:id" element={<GiveawayDetails />} />
          <Route path="/giveaway/:id/trust" element={<GiveawayTrust />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/create" element={<CreateGiveaway />} />
          <Route path="/my-giveaways" element={<MyGiveaways />} />
          <Route path="/my-channels" element={<MyChannels />} />
          <Route path="/channels/:id/analytics" element={<ChannelAnalytics />} />
          <Route path="/templates" element={<PostTemplates />} />
          <Route path="/join-captcha/:nonce" element={<JoinCaptcha />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

function App() {
  return (
    <>
      <AppRoutes />
      <Toaster position="top-center" richColors />
    </>
  )
}

export default App
